import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { usePublicClient } from 'wagmi';
import { loadHistory, recordHistoryEntry } from '../lib/localHistory';
import { isStuckState, stateLabel } from '../lib/bridgeApi';
import { BRIDGE_RECEIVER_ABI } from '../lib/bridgeCalldata';
import { chainName, kStableInfo } from '../_shared/wallet-app/chains';
import { formatUnits } from '../lib/wei';
import { BRAND } from '../theme';

// BridgeHistoryPage
//
// There is no server-side bridge history in the live architecture (see
// lib/bridgeApi.js) — the customer's wallet signs everything directly and
// delivery is observed trustlessly on-chain. This page shows history for
// THIS BROWSER only (lib/localHistory.js), refreshed on load with a
// best-effort trustless re-check of `fulfilled(swapId)` for any entry not
// yet marked delivered — the exact same on-chain read useBridgeDelivery does
// on the status page, just run once per entry here.
//
// Phase 1 only has two destination chains (Base, Arbitrum), so we can read
// two fixed publicClients unconditionally instead of calling a hook inside a
// loop (which would violate the rules of hooks).

const STATE_CHIP_COLOR = {
  mint_confirmed: 'success',
  burn_confirmed: 'primary',
  burn_submitted: 'info',
  intent_created: 'default',
};

export default function BridgeHistoryPage({ onOpen }) {
  const [entries, setEntries] = useState(() => loadHistory());
  const publicClientBase = usePublicClient({ chainId: 8453 });
  const publicClientArbitrum = usePublicClient({ chainId: 42161 });

  const clientForChain = (chainId) => {
    if (Number(chainId) === 8453) return publicClientBase;
    if (Number(chainId) === 42161) return publicClientArbitrum;
    return null;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const current = loadHistory();
      const refreshed = await Promise.all(
        current.map(async (e) => {
          if (e.state === 'mint_confirmed' || !e.swapId || !e.dstBridgeReceiverAddress) {
            return e;
          }
          const client = clientForChain(e.dstChainId);
          if (!client) return e;
          try {
            const isFulfilled = await client.readContract({
              address: e.dstBridgeReceiverAddress,
              abi: BRIDGE_RECEIVER_ABI,
              functionName: 'fulfilled',
              args: [e.swapId],
            });
            if (isFulfilled) {
              const patched = { ...e, state: 'mint_confirmed' };
              recordHistoryEntry(patched);
              return patched;
            }
          } catch (_) {
            // Transient RPC hiccup — show the last known state.
          }
          return e;
        }),
      );
      if (!cancelled) setEntries(refreshed);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicClientBase, publicClientArbitrum]);

  return (
    <Box sx={{ maxWidth: 1080, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 0.5, fontWeight: 700 }}>
        Your bridges
      </Typography>
      <Typography variant="body2" sx={{ color: BRAND.textSecondary, mb: 3 }}>
        Every bridge started from this browser. Delivery status is checked
        live on-chain — nothing here comes from a server.
      </Typography>

      <Card>
        <CardContent>
          {entries.length === 0 && (
            <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
              <Typography variant="h6">No bridges yet</Typography>
              <Typography variant="body2" sx={{ color: BRAND.textSecondary }}>
                Compose your first bridge from the Bridge tab.
              </Typography>
            </Stack>
          )}
          {entries.length > 0 && (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>kStable</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Started</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((it) => {
                    const id = it.localId;
                    const sym = it.kStableSymbol || '';
                    const dec = kStableInfo(sym)?.decimals ?? 6;
                    return (
                      <TableRow
                        key={id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => onOpen?.(id)}
                      >
                        <TableCell sx={{ fontFamily: 'monospace' }}>
                          {id?.slice(0, 8)}…
                        </TableCell>
                        <TableCell>{sym}</TableCell>
                        <TableCell align="right">
                          {it.amountWei ? formatUnits(it.amountWei, dec) : '—'}
                        </TableCell>
                        <TableCell>{chainName(it.srcChainId)}</TableCell>
                        <TableCell>{chainName(it.dstChainId)}</TableCell>
                        <TableCell>
                          <Chip
                            label={stateLabel(it.state)}
                            size="small"
                            color={
                              isStuckState(it.state)
                                ? 'warning'
                                : STATE_CHIP_COLOR[it.state] || 'default'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {it.requestedAt ? new Date(it.requestedAt * 1000).toLocaleString() : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
